import {
  AssetChatSubject,
  LocationInput,
  MediaData,
  MediaPickerComponent,
  Severity,
  useCreateChatActivity,
} from '@gripp/shared-logic';
import { QueryKey } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Toast from 'react-native-toast-message';
import { useTypedMedia } from '../media/useTypedMedia';
import { ChatComposerCard, useComposerLocation } from './chatComposerCard';

export type AssetChatComposerProps = {
  /** The asset and workspace a post is filed against. */
  subject: Pick<AssetChatSubject, 'assetId' | 'workspaceId'>;
  /** Named in the placeholder ("Message 1795-9…"). */
  assetName?: string;
  /** The feed's query key, so a post shows before the server answers. */
  feedCacheKey?: QueryKey;
  /** Platform media picker; the host supplies web's or native's. */
  mediaPicker: MediaPickerComponent;
  testID?: string;
};

/**
 * The asset chat's composer. Writing here creates the same `Issue` the classic
 * activity list shows: see `useCreateChatActivity`, which is where that
 * equivalence is enforced. The card itself is shared with an activity's own
 * chat, see `ChatComposerCard`.
 */
export const AssetChatComposer = ({
  subject,
  assetName,
  feedCacheKey,
  mediaPicker,
  testID,
}: AssetChatComposerProps) => {
  const { t } = useTranslation();
  const { createChatActivity } = useCreateChatActivity({
    subject,
    feedCacheKey,
  });
  const requestLocation = useComposerLocation();
  const { typedMedia, handleAdd, handleDelete, handleUploadProgress, reset } =
    useTypedMedia<MediaData>([]);

  const [message, setMessage] = useState('');
  const [sendAsSeverity, setSendAsSeverity] = useState<Severity>();
  const [location, setLocation] = useState<LocationInput>();
  const [isUploading, setUploading] = useState(false);
  // Guards a second send while the first is still in flight: the draft is
  // cleared optimistically, so without this a double press could post twice.
  const [isSending, setIsSending] = useState(false);

  // Trimmed, unlike the legacy footer's untrimmed check: whitespace alone is
  // not a message, and the send control's colour is derived from this.
  const canSend =
    (message.trim().length > 0 || typedMedia.length > 0) &&
    !isUploading &&
    !isSending;

  const send = async () => {
    if (!canSend || isSending) return;
    const outgoing = message;
    const outgoingMedia = typedMedia;
    const outgoingSeverity = sendAsSeverity;
    const outgoingLocation = location;
    // Cleared immediately, the way a chat composer should feel, but restored
    // below if the post fails, because otherwise "try again" means retyping
    // the message and re-attaching every file.
    setMessage('');
    reset();
    setIsSending(true);
    try {
      await createChatActivity({
        message: outgoing,
        severity: outgoingSeverity,
        media: outgoingMedia,
        location: outgoingLocation,
      });
      // Both modifiers are per-post, not sticky: the next message in a
      // conversation is an ordinary message unless the author says otherwise,
      // and a stale "Posting as Urgent" would file the wrong record silently.
      setSendAsSeverity(undefined);
      setLocation(undefined);
    } catch (error) {
      // Put the draft back exactly as it was, modifiers included, so the
      // author can retry rather than reconstruct.
      setMessage(outgoing);
      outgoingMedia.forEach((item) => handleAdd(item));
      setSendAsSeverity(outgoingSeverity);
      setLocation(outgoingLocation);
      Toast.show({ type: 'error', text1: t('chat.composer.postFailed') });
    } finally {
      setIsSending(false);
    }
  };

  const onLocationPress = async () => {
    if (location) {
      setLocation(undefined);
      return;
    }
    const fix = await requestLocation();
    if (fix) setLocation(fix.location);
  };

  return (
    <ChatComposerCard
      message={message}
      onMessageChange={setMessage}
      onSend={() => void send()}
      canSend={canSend}
      placeholder={
        assetName
          ? t('chat.composer.placeholder', { asset: assetName })
          : t('chat.composer.placeholderFallback')
      }
      media={typedMedia}
      onMediaAdded={handleAdd}
      onMediaDeleted={handleDelete}
      onUploadProgress={handleUploadProgress}
      onUploadingChange={(_source, busy) => setUploading(busy)}
      mediaPicker={mediaPicker}
      workspaceId={subject.workspaceId}
      onLocationPress={() => void onLocationPress()}
      attachedLocation={
        location ? { onClear: () => setLocation(undefined) } : undefined
      }
      sendAs={{ severity: sendAsSeverity, onChange: setSendAsSeverity }}
      testID={testID}
    />
  );
};
